/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import {
  Person,
  Business,
  Phone,
  Email,
  LocationOn,
  Description,
  CalendarToday,
  AttachMoney,
  Article,
  CheckCircle,
  Cancel,
  AccessTime,
  Assignment,
  Domain,
  ContactPhone,
  Numbers,
  LibraryBooks,
  LocalParking,
  HomeWork,
  Group,
  Apartment
} from '@mui/icons-material';
import GetAppIcon from '@mui/icons-material/GetApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StraightenIcon from '@mui/icons-material/Straighten';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ScaleIcon from '@mui/icons-material/Scale';
import RuleIcon from '@mui/icons-material/Rule';
import PetsIcon from '@mui/icons-material/Pets';
import WcIcon from '@mui/icons-material/Wc';
import CarCrashIcon from '@mui/icons-material/CarCrash';
import ForestIcon from '@mui/icons-material/Forest';
import NumbersIcon from '@mui/icons-material/Numbers';
import MoneyIcon from '@mui/icons-material/Money';
import FiberSmartRecordIcon from '@mui/icons-material/FiberSmartRecord';
import EmergencyIcon from '@mui/icons-material/Emergency';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { styled } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import ViewMachineDetails from 'src/pages/Permits/ClerkVending/ViewMachineDetails';
import { getApplicationDetails } from 'src/pages/Permits/common/getApplicationDetails';
import { DraftCheck } from 'src/types/PZPermit/pzPermit.type';
import CustomButtonContained from 'src/components/CustomButton/CustomButtonContained';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
import CustomModal from '../customModal/CustomModal';
import EditFormfeatureViews from 'src/components/EditFormfeatureViews/EditFormfeatureViews';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { shouldShowEdit } from 'src/utils/CommonFunctions';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import WarningIcon from '@mui/icons-material/Warning';
import { printApplication } from 'src/ui-component/ApplicationActions/printApplication';

interface PermitId {
  permitId: number;
}
// import { downloadApplication } from '../ApplicationActions/downloadApplication';
import FeeSummaryPage from 'src/pages/Permits/Construction/FeeSummaryPage';
import ScheduleInspectionModal from 'src/pages/Permits/Construction/Inspection/ScheduleInspectionModal';
import CertificateOfApproval from 'src/pages/Permits/Construction/utils/CertificateOfApproval';
import SoftApprovalSection from 'src/pages/Permits/Construction/SoftApproval/SoftApprovalSection';
import { getFeesForCurrentPermit } from 'src/pages/Permits/common/getFeesForCurrentPermit';
import PaymentTypeModal, { PetFeeBreakdown } from '../PaymentTypeModal';
import { getLateFeeAmount, isPetLateFeesWaived } from 'src/components/common/calculatePetFee';
import {
  addPetLicenseToCart,
  clearPetLicenseFromCart,
  selectPetLicenseCartItems
} from 'src/redux-store/slices/cartSlice';
import type { PetLicenseCartItem } from 'src/utils/petLicenseCart';
import { canUsePetLicenseBundleCheckout } from 'src/utils/petLicenseBundleEligibility';

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(3)
}));

// const FieldGroup = styled(Box)(({ theme }) => ({
//   display: 'grid',
//   gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
//   gap: theme.spacing(3),
//   marginBottom: theme.spacing(2)
// }));
const FieldGroup = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)', // Changed from auto-fill, minmax(300px, 1fr) to 3 equal columns
  gap: theme.spacing(3),
  marginBottom: theme.spacing(2),
  // Add responsive behavior for smaller screens
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)' // 2 columns on medium screens
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr' // 1 column on small screens
  }
}));

const DetailField = ({
  label,
  value,
  icon,
  index,
  relevantFields,
  showFeeBreakdownInfo,
  onFeeBreakdownClick,
  feeBreakdownLoading
}: {
  label: string;
  value: any;
  icon: React.ReactElement;
  index: number;
  relevantFields: any;
  showFeeBreakdownInfo?: boolean;
  onFeeBreakdownClick?: () => void;
  feeBreakdownLoading?: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      mb: relevantFields?.length - 1 === index ? 0 : 2
    }}
  >
    <Box sx={{ mr: 2, color: 'primary.main' }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        color="textSecondary"
        sx={{
          fontSize: '0.875rem',
          fontWeight: 500,
          textTransform: 'capitalize'
        }}
      >
        {label.replace(/([A-Z])/g, ' $1').trim()}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontWeight: 400 }}>{value}</Typography>
        {showFeeBreakdownInfo && onFeeBreakdownClick && (
          <Tooltip title="View fee breakdown">
            <IconButton
              size="small"
              onClick={onFeeBreakdownClick}
              disabled={feeBreakdownLoading}
              sx={{ p: 0.25 }}
            >
              <InfoIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  </Box>
);
type Field = {
  key: string;
  value: any;
  icon: React.ReactElement;
};

type StatusConfig = {
  color: 'success' | 'error' | 'warning' | 'default' | 'info' | 'primary';
  icon: React.ReactElement;
};

function ViewApplication({ StaticDetails }: { StaticDetails?: any }) {
  const location = useLocation();
  const navigate = useNavigate();
  const messageBodyRef = useRef();
  const dispatch = useDispatch();
  const { townshipId, applicationID, permit } = useParams();
  const [searchParams] = useSearchParams();

  // Get optional boolean query parameter for payment status (defaults to false if not provided)
  // Usage: /view-application/44?paymentStatus=true or /view-application/44?paymentStatus=false
  // If not provided, defaults to false and component works as before
  // Handle malformed URLs where paymentStatus might be duplicated or encoded incorrectly
  const paymentStatusParam = searchParams.get('paymentStatus') || '';
  // Also check location.search as fallback for malformed URLs (browser decodes URL automatically)
  const locationHasPaymentStatus =
    location.search.includes('paymentStatus=true');
  const paymentStatus =
    paymentStatusParam === 'true' ||
    paymentStatusParam.startsWith('true') ||
    locationHasPaymentStatus;

  const [data, setData] = useState<any>(null);
  const [files, setFiles] = useState<any>([]);
  const [switchTo, setSwitchTo] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isPetPaymentProcessing, setIsPetPaymentProcessing] =
    useState<boolean>(false);
  const [IsOpen, setIsOpen] = useState<boolean>(false);
  const [inspectionModalOpen, setInspectionModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentConfirmationModalOpen, setPaymentConfirmationModalOpen] =
    useState<boolean>(false);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [paymentTypeModalOpen, setPaymentTypeModalOpen] = useState<boolean>(false);
  const [calculatedTotalAmount, setCalculatedTotalAmount] = useState<number>(0);
  const [petFeeBreakdown, setPetFeeBreakdown] = useState<PetFeeBreakdown | undefined>(undefined);
  const [feeBreakdownDialogOpen, setFeeBreakdownDialogOpen] = useState(false);
  const [viewFeeBreakdown, setViewFeeBreakdown] = useState<PetFeeBreakdown | null>(null);
  const [viewFeeBreakdownLoading, setViewFeeBreakdownLoading] = useState(false);
  const [viewFeeTotalAmount, setViewFeeTotalAmount] = useState<number>(0);
  const petPaymentAmountsRef = useRef<{ applicationFee: number; totalAmount: number; taxRate: number } | null>(null);
  const isDraft = location?.state && (location?.state as DraftCheck)?.isDraft;
  const permitId = location?.state && (location?.state as PermitId)?.permitId;
  const currentPermitRole = useSelector(
    (state: RootState) =>
      state?.currentPermit?.currentPermitDetails?.currentPermitUserRole
  );
  const currentPermitDetails = useSelector(
    (state: RootState) => state?.currentPermit?.currentPermitDetails
  );
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const petLicenseCartItems = useSelector(selectPetLicenseCartItems);
  const isManager = currentPermitRole === 'Manager';
  const isUser = currentPermitRole === 'Vendor';
  // Check if user is Manager or Admin (for payment type selection)
  const isManagerOrAdmin = isManager || userData?.UserRole === 'Admin' || userData?.UserRole === 'Super Admin';

  const canBundleCheckout = canUsePetLicenseBundleCheckout(
    currentPermitRole,
    userData?.UserRole
  );
  const isPetInCart = petLicenseCartItems.some(
    (p) => Number(p.ApplicationID) === Number(applicationID)
  );

  const handleAddToPetLicenseCart = (): void => {
    if (!data) return;
    if (isPetInCart) {
      toast.error('Already in cart');
      return;
    }
    const cartItem: PetLicenseCartItem = {
      ApplicationID: Number(data.ApplicationID ?? applicationID),
      ApplicationNumber: data.ApplicationNumber,
      PetName: data.PetName,
      PetType: data.PetType,
      OwnerName: data.OwnerName,
      UserID: data.UserID,
      IsSpayedNeutered: data.IsSpayedNeutered,
      ApplicationDate: data.ApplicationDate,
      CreateDate: data.CreateDate,
      StateRegistrationFee: data.StateRegistrationFee,
      PilotClinicFee: data.PilotClinicFee,
      LateFeesWaived: data.LateFeesWaived
    };
    dispatch(addPetLicenseToCart(cartItem));
    const label = data.PetName || data.ApplicationNumber || 'Application';
    toast.success(`${label} added to cart!`);
  };

  const handleGetApplicationDetails = async () => {
    const response = await getApplicationDetails({
      applicationID,
      permit,
      isDraft
    });
    if (response?.status === 'success') {
      setData(response?.data?.data[0]);
    }
  };

  const getUploadedDocuments = async () => {
    const response = await httpRequest(
      'GET',
      `/uploadPDF/?ApplicationID=${applicationID}&PermitID=${permit}`
    );
    const dOcs = await response?.data;
    if (response?.status === 'success') {
      const abstractedData =
        dOcs &&
        dOcs?.length &&
        dOcs?.map((item) => {
          return {
            FileName: item?.DocumentName,
            Type: item.DocumentType,
            downloadURL: item.downloadURL,
            ApplicationDocsID: item.ApplicationDocsID
          };
        });
      setFiles(abstractedData);
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  useEffect(() => {
    if (StaticDetails) {
      setData(StaticDetails);
      setFiles(StaticDetails?.DocumentFileUpload);
    } else {
      handleGetApplicationDetails();
      if (Number(permit) !== 8) getUploadedDocuments();
    }
  }, [applicationID, permit]);

  // Handle payment status callback - make API call when paymentStatus=true
  // Use a ref to prevent multiple API calls
  const hasCalledPaymentAPI = useRef(false);

  useEffect(() => {
    const fetchCartPayments = async () => {
      // Check if paymentStatus is true and we haven't called the API yet
      if (paymentStatus && applicationID && !hasCalledPaymentAPI.current) {
        try {
          hasCalledPaymentAPI.current = true; // Mark as called to prevent duplicate calls
          setLoading(true);
          console.log(
            'Calling getCartPayments API for ApplicationID:',
            applicationID
          );
          const response = await httpRequest(
            'GET',
            `/payments/getCartPayments?ApplicationID=${applicationID}`
          );

          // Log the full response for debugging
          console.log('Full getCartPayments response:', response);
          console.log('Response status:', response?.status);
          console.log('Response data:', response?.data);
          console.log('Response data.data:', response?.data?.data);
          console.log('Response data.data.data:', response?.data?.data?.data);

          // Check if response is successful (handle multiple status formats)
          const isSuccess =
            response?.status === 'success' ||
            response?.status === 'Success' ||
            response?.data?.status_code === '200' ||
            response?.data?.status === 'Payment completed successfully.';

          if (isSuccess) {
            // Extract payment details from response
            // Based on API response structure: response.data.data contains the actual payment data
            // Structure: { status: "success", data: { status_code: "200", data: { billing_amount, billing_service_fee, ... } } }
            const paymentData = response?.data?.data || null;

            // Log the payment data for debugging
            console.log('Payment data from API:', paymentData);
            console.log('Payment data path check:', {
              'response.data': response?.data,
              'response.data.data': response?.data?.data,
              billing_amount: paymentData?.billing_amount,
              billing_service_fee: paymentData?.billing_service_fee
            });

            // Ensure we have valid paymentData before accessing properties
            if (!paymentData || !paymentData.billing_amount) {
              console.error(
                'No payment data found in response. Response structure:',
                {
                  status: response?.status,
                  data: response?.data,
                  'data.data': response?.data?.data,
                  paymentData: paymentData
                }
              );
              setLoading(false);
              toast.error('Payment data not found in response.');
              dispatch(handleError('Payment data not found in response.'));
              return;
            }

            // Use billing amount and billing service fee from API response (snake_case fields)
            const billingAmount = parseFloat(
              paymentData?.billing_amount || paymentData?.BillingAmount || 0
            );
            const billingServiceFee = parseFloat(
              paymentData?.billing_service_fee ||
                paymentData?.BillingServiceFee ||
                0
            );

            // Calculate amounts
            // billingAmount is the base amount, billingServiceFee is additional
            const totalAmountPayable = billingAmount + billingServiceFee; // Total amount (base + service fee)
            const totalAmountWithoutTax = billingAmount; // Base amount without service fee
            const totalTaxAmount = billingServiceFee; // Service fee amount

            // Get user and application details from payment data (snake_case fields)
            const userName =
              `${paymentData?.billing_first_name || ''} ${
                paymentData?.billing_last_name || ''
              }`.trim() ||
              `${userData?.FirstName || ''} ${
                userData?.LastName || ''
              }`.trim() ||
              data?.OwnerName ||
              'User';
            const applicationNumber =
              paymentData?.confirmation_number ||
              paymentData?.ConfirmationNumber ||
              data?.ApplicationNumber ||
              '';
            const departmentName =
              currentPermitDetails?.departmentName || 'Clerk Department';

            // Store payment details and open confirmation modal
            setPaymentDetails({
              userName,
              applicationNumber,
              totalAmountPayable,
              totalAmountWithoutTax,
              totalTaxAmount,
              departmentName,
              paymentData // Store full payment data for additional details
            });
            setLoading(false);
            // Show success message
            toast.success('Payment information retrieved successfully.');
            setPaymentConfirmationModalOpen(true);

            if (permit === '10') {
              dispatch(clearPetLicenseFromCart());
            }

            // Remove paymentStatus query parameter from URL to prevent showing modal again on refresh
            // Use setTimeout to avoid triggering useEffect again immediately
            setTimeout(() => {
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.delete('paymentStatus');
              const newUrl = `${window.location.pathname}${
                newSearchParams.toString()
                  ? '?' + newSearchParams.toString()
                  : ''
              }`;
              window.history.replaceState({}, '', newUrl);
            }, 100);
          } else {
            // Log the error response for debugging
            console.error('getCartPayments API failed:', {
              status: response?.status,
              message: response?.message,
              data: response?.data,
              fullResponse: response
            });
            setLoading(false);
            toast.error(
              response?.message || 'Failed to retrieve payment information.'
            );
            dispatch(
              handleError(
                response?.message || 'Failed to retrieve payment information.'
              )
            );
          }
        } catch (error) {
          console.error('Error fetching cart payments:', error);
          setLoading(false);
          toast.error(
            'Failed to retrieve payment information. Please try again.'
          );
          dispatch(
            handleError(
              'Failed to retrieve payment information. Please try again.'
            )
          );
        }
      }
    };

    fetchCartPayments();
  }, [paymentStatus, applicationID]);

  // Reset the ref when the component unmounts or when applicationID changes
  useEffect(() => {
    hasCalledPaymentAPI.current = false;
  }, [applicationID]);

  if (!data) {
    return <LoadingOverlay />;
  }

  // Show loader for pet permit (ID 10) payment processing (when clicking proceed to payment)
  if (isPetPaymentProcessing && permit === '10') {
    return <LoadingOverlay />;
  }

  // Show loader when paymentStatus=true and API is being called
  if (paymentStatus && loading && permit === '10') {
    return <LoadingOverlay />;
  }

  const formatValue = (key: string, value: any): string | null => {
    const isDateField = key.toLowerCase().includes('date') || key === 'PetDOB';
    if (value === null || value === undefined || value === '') {
      return isDateField ? '' : null;
    }
    // Handle boolean-like values (DB may return true/false, 1/0, or "true"/"false"/"yes")
    if (key === 'IsSpayedNeutered' || typeof value === 'boolean') {
      const isTrue =
        value === true ||
        value === 1 ||
        value === '1' ||
        value === 'true' ||
        String(value).toLowerCase() === 'yes';
      return isTrue ? 'Yes' : 'No';
    }
    if (key.toLowerCase().includes('fee') && !isNaN(value) && value > 0) {
      return `$${parseFloat(value).toFixed(2)}`;
    }

    if (key === 'PetWeight') {
      return `${value} Lbs`;
    }
    if (key.toLowerCase().includes('date') || key === 'PetDOB') {
      if (value === null || value === undefined || value === '' || value === 0) return '';
      try {
        const date = new Date(value);
        if (isNaN(date.getTime()) || date.getTime() === 0) return '';
        return format(date, 'MM/dd/yyyy');
      } catch (error) {
        return '';
      }
    }
    return String(value);
  };

  const getStatusConfig = (status: string): StatusConfig => {
    const statusConfigs: Record<string, StatusConfig> = {
      approved: { color: 'success', icon: <CheckCircle /> },
      rejected: { color: 'error', icon: <Cancel /> },
      pending: { color: 'warning', icon: <AccessTime /> },
      InReview: { color: 'warning', icon: <AccessTime /> },
      withdrawn: { color: 'error', icon: <Cancel /> },
      'await co': { color: 'warning', icon: <AccessTime /> },
      'await vendor': { color: 'primary', icon: <AccessTime /> },
      'in review': { color: 'warning', icon: <AccessTime /> },
      'in review l1': { color: 'warning', icon: <AccessTime /> },
      'in review l2': { color: 'warning', icon: <AccessTime /> },
      'approved closed': { color: 'info', icon: <AccessTime /> },
      issued: { color: 'success', icon: <CheckCircle /> }
    };
    return (
      statusConfigs[status?.toLowerCase()] || {
        color: 'default',
        icon: <Article />
      }
    );
  };

  const addFieldIfExists = (
    fields: Field[],
    key: string,
    icon: React.ReactElement
  ) => {
    const value = formatValue(key, data[key]);
    if (value !== null) {
      if (
        (key === 'WorkDate' ||
          key === 'DateOfIncorporation' ||
          key === 'ApprovedDate') &&
        !value
      ) {
        return;
      }
      fields.push({ key, value, icon });
    }
  };

  const getRelevantFields = (): Field[] => {
    const fields: Field[] = [];
    const fieldConfigs = [
      { key: 'ApplicationType', icon: <Assignment /> },
      { key: 'ApplicantType', icon: <Assignment /> },
      { key: 'ApplicationTitle', icon: <Description /> },
      { key: 'ApplicantName', icon: <Person /> },
      { key: 'ApplicantPhoneNo', icon: <Phone /> },
      { key: 'ApplicantAddress', icon: <LocationOn /> },
      { key: 'OwnerName', icon: <Person /> },
      { key: 'OwnerPhoneNumber', icon: <Phone /> },
      { key: 'OwnerContactNumber', icon: <Phone /> },
      { key: 'OwnerEmailAddress', icon: <Email /> },
      { key: 'OwnerAddress', icon: <LocationOn /> },
      { key: 'PetType', icon: <PetsIcon /> },
      { key: 'PetName', icon: <DriveFileRenameOutlineIcon /> },
      { key: 'PetSex', icon: <WcIcon /> },
      { key: 'PetHair', icon: <InfoIcon /> },
      { key: 'PetBreed', icon: <PetsIcon /> },
      { key: 'IsSpayedNeutered', icon: <RuleIcon /> },
      { key: 'PetDOB', icon: <CalendarToday /> },
      { key: 'ChipNumber', icon: <NumbersIcon /> },
      { key: 'PetColor', icon: <ColorLensIcon /> },
      { key: 'PetWeight', icon: <ScaleIcon /> },
      { key: 'RabiesIssueDate', icon: <CalendarToday /> },
      { key: 'RabiesExpirationDate', icon: <CalendarToday /> },
      { key: 'VetClinic', icon: <EmergencyIcon /> },
      { key: 'VetPhoneNumber', icon: <ContactPhone /> },
      { key: 'NearestCrossStreet', icon: <LocationOn /> },
      { key: 'OrganizationName', icon: <BusinessIcon /> },
      { key: 'Contractor', icon: <Person /> },
      { key: 'ContactPersonName', icon: <Person /> },
      { key: 'Address', icon: <LocationOn /> },
      { key: 'EmailAddress', icon: <Email /> },
      { key: 'PhoneNumber', icon: <Phone /> },
      { key: 'NotProfitIdNumber', icon: <NumbersIcon /> },
      { key: 'ProofOfNotProfitType', icon: <MoneyIcon /> },
      { key: 'WalmartUsed', icon: <FiberSmartRecordIcon /> },
      { key: 'ContractorAddress', icon: <LocationOn /> },
      { key: 'CompanyName', icon: <Business /> },
      { key: 'ProjectContactName', icon: <Person /> },
      { key: 'ProjectContactPhone', icon: <ContactPhone /> },
      { key: 'CompanyAddress', icon: <LocationOn /> },
      { key: 'CompanyEmail', icon: <Email /> },
      { key: 'CompanyPhoneNumber', icon: <Phone /> },
      { key: 'ContactName', icon: <Person /> },
      { key: 'StateOfIncorporation', icon: <NumbersIcon /> },
      { key: 'Blocklots', icon: <Domain /> },
      { key: 'BlockLots', icon: <Domain /> },
      { key: 'BlockLot', icon: <Domain /> },
      { key: 'LicensedAddress', icon: <LocationOn /> },
      { key: 'NoOfTrees', icon: <ForestIcon /> },
      { key: 'LotSize', icon: <AspectRatioIcon /> },
      { key: 'PropertyAddress', icon: <LocationOn /> },
      { key: 'ProjectContactAddress', icon: <LocationOn /> },
      { key: 'WorkDate', icon: <CalendarToday /> },
      { key: 'DateOfIncorporation', icon: <CalendarToday /> },
      { key: 'AgentNameOfCorporation', icon: <Person /> },
      { key: 'RoadOpeningLocation', icon: <LocationOn /> },
      { key: 'DescribeUse', icon: <Description /> },
      { key: 'EIN', icon: <Numbers /> },
      { key: 'FtWide', icon: <StraightenIcon /> },
      { key: 'FtLong', icon: <StraightenIcon /> },
      { key: 'FtDeep', icon: <StraightenIcon /> },
      { key: 'TotalSqFootage', icon: <StraightenIcon /> },
      { key: 'SquareFeet', icon: <StraightenIcon /> },
      { key: 'Emergency', icon: <EmergencyIcon /> },
      { key: 'SpecialConditions', icon: <InfoIcon /> },
      { key: 'ServiesTypesToSold', icon: <InfoIcon /> },
      { key: 'ProcessOfSoldAndDeliver', icon: <InfoIcon /> },
      { key: 'VehicleUsed', icon: <CarCrashIcon /> },
      { key: 'Purpose', icon: <InfoIcon /> },
      { key: 'RemovalPurpose', icon: <InfoIcon /> },
      //construction permit data
      { key: 'AgentLicenseNumber', icon: <Numbers /> },
      { key: 'AgriculturalEquipmentSpecs', icon: <Description /> },
      { key: 'ArchitectLicenseNumber', icon: <Numbers /> },
      { key: 'AuthorizedEmail', icon: <Email /> },
      { key: 'AuthorizedRepresentative', icon: <Person /> },
      { key: 'BusinessRegistrationNumber', icon: <Numbers /> },
      { key: 'ContractorLicenseNumber', icon: <Numbers /> },
      { key: 'CorporateEntityName', icon: <Business /> },
      { key: 'CorporateTaxInfo', icon: <Description /> },
      { key: 'GovernmentAgencyName', icon: <Domain /> },
      { key: 'IndustrialEquipmentSpecs', icon: <Description /> },
      { key: 'LandUseCropPlan', icon: <Description /> },
      { key: 'NumberOfBedrooms', icon: <Numbers /> },
      { key: 'NumberOfUnits', icon: <Numbers /> },
      { key: 'TenantEmail', icon: <Email /> },
      { key: 'TenantName', icon: <Person /> },
      { key: 'TenantPhone', icon: <Phone /> },
      { key: 'projectLocation', icon: <LocationOn /> },
      { key: 'BusinessOccupancyClassification', icon: <LibraryBooks /> },
      { key: 'ParkingRequirements', icon: <LocalParking /> },
      { key: 'ResidentialUnitBreakdown', icon: <Apartment /> },
      { key: 'CommercialUnitBreakdown', icon: <Business /> },
      { key: 'SharedFacilityPlan', icon: <HomeWork /> },
      { key: 'OwnersName', icon: <Person /> },
      { key: 'OwnerContactInfo', icon: <ContactPhone /> },
      { key: 'PropertyType', icon: <Assignment /> },
      { key: 'PropertyUseType', icon: <Assignment /> },
      { key: 'PropertyUsePlan', icon: <Description /> },
      { key: 'PropertyTaxInfo', icon: <Description /> },
      { key: 'RegisteredPartnershipNumber', icon: <Numbers /> },
      { key: 'PartnerNames', icon: <Group /> },
      { key: 'PartnershipTaxInfo', icon: <Description /> },
      { key: 'TrusteeName', icon: <Person /> },
      { key: 'BeneficiariesInfo', icon: <Group /> },
      { key: 'EstateTaxInfo', icon: <Description /> },
      { key: 'SharedFacilityManagementPlan', icon: <HomeWork /> },
      { key: 'ConstructionSchedule', icon: <CalendarToday /> }
    ];

    fieldConfigs.forEach(({ key, icon }) => {
      addFieldIfExists(fields, key, icon);
    });

    const status = data.ApplicationStatus?.toLowerCase();
    switch (status) {
      case 'approved':
        addFieldIfExists(fields, 'ApprovedDate', <CalendarToday />);
        addFieldIfExists(fields, 'ApprovalNote', <Description />);
        break;
      case 'issued':
        addFieldIfExists(fields, 'IssuedDate', <CalendarToday />);
        addFieldIfExists(fields, 'ApprovedDate', <CalendarToday />);
        addFieldIfExists(fields, 'ApprovalNote', <Description />);
        break;
      case 'rejected':
        addFieldIfExists(fields, 'RejectedDate', <CalendarToday />);
        addFieldIfExists(fields, 'ReasonOfRejection', <Description />);
        break;
      case 'withdrawn':
        addFieldIfExists(fields, 'WithdrawDate', <CalendarToday />);
        break;
    }

    ['ApplicationFee', 'InspectionFee'].forEach((fee) => {
      addFieldIfExists(fields, fee, <AttachMoney />);
    });

    ['CreateDate', 'UpdateDate'].forEach((date) => {
      addFieldIfExists(fields, date, <CalendarToday />);
    });

    return fields;
  };

  const handleMessageChange = (e) => {
    messageBodyRef.current = e.target.value;
  };

  const relevantFields = getRelevantFields();
  const statusConfig = getStatusConfig(data.ApplicationStatus);

  // Handle Credit Card payment (existing flow)
  const handleCreditCardPayment = async (): Promise<void> => {
    try {
      setIsPetPaymentProcessing(true);

      let applicationFee: number;
      let totalAmount: number;
      let taxRate = 0;

      // Use pre-calculated pet fee breakdown if available (from petPermitProceedToPayment)
      if (permit === '10' && petPaymentAmountsRef.current) {
        applicationFee = petPaymentAmountsRef.current.applicationFee;
        totalAmount = petPaymentAmountsRef.current.totalAmount;
        taxRate = petPaymentAmountsRef.current.taxRate;
      } else {
        const feeResponse = await httpRequest(
          'GET',
          `/configuration/fee_details?PermitID=${permit}`
        );
        if (feeResponse?.status === 'success') {
          const feeObject = getFeesForCurrentPermit(feeResponse.data);
          taxRate = feeObject?.CreditCardFee || 0;
        }
        applicationFee = data?.ApplicationFee ? parseFloat(data.ApplicationFee) : 0;
        const inspectionFee = data?.InspectionFee ? parseFloat(data.InspectionFee) : 0;
        const subtotal = applicationFee + inspectionFee;
        const taxAmount = subtotal * (taxRate / 100);
        totalAmount = subtotal + taxAmount;
      }

      // Get current page route (origin + pathname) for return URL
      const returnUrl = window.location.origin + window.location.pathname;
      const departmentName =
        currentPermitDetails?.departmentName || 'Clerk Department';

      const inspectionFee = permit === '10' ? 0 : (data?.InspectionFee ? parseFloat(data.InspectionFee) : 0);
      const subtotal = applicationFee + inspectionFee;
      const taxAmount = totalAmount - subtotal;

      // Prepare comprehensive payload for createCart API with all details
      const createCartPayload = {
        PermitID: Number(permit),
        ApplicationID: Number(applicationID),
        CartApplicationIDs: [Number(applicationID)],
        ApplicationNumber: data?.ApplicationNumber || null,
        ApplicationStatus: data?.ApplicationStatus || 'Approved',
        PermitPrefix: 'Pet',
        UserID: data?.UserID || userData?.UserID || null,
        UserName: `${userData?.FirstName || ''} ${
          userData?.LastName || ''
        }`.trim(),
        UserEmail: userData?.EmailAddress || data?.OwnerEmailAddress || null,
        UserPhone: userData?.PhoneNumber || data?.OwnerContactNumber || null,
        ApplicationFee: applicationFee,
        InspectionFee: inspectionFee,
        TaxRateAmount: taxRate,
        TotalAmount: totalAmount,
        Subtotal: subtotal,
        TaxAmount: taxAmount,
        ApplicantName: data?.ApplicantName || data?.OwnerName || null,
        OwnerName: data?.OwnerName || null,
        OwnerAddress: data?.OwnerAddress || null,
        OwnerEmailAddress: data?.OwnerEmailAddress || null,
        OwnerContactNumber: data?.OwnerContactNumber || null,
        ApplicationType: data?.ApplicationType || null,
        PetName: data?.PetName || null,
        PetBreed: data?.PetBreed || null,
        PetType: data?.PetType || null,
        TownshipID: Number(townshipId) || null,
        DepartmentName: departmentName,
        ReturnUrl: returnUrl
      };

      // Call createCart API
      const cartResponse = await httpRequest(
        'POST',
        '/payments/createCart',
        createCartPayload
      );

      if (
        cartResponse?.status === 'success' &&
        cartResponse?.data?.data?.checkout_link
      ) {
        window.location.href = cartResponse.data.data.checkout_link;
      } else {
        setIsPetPaymentProcessing(false);
        toast.error(
          cartResponse?.message || 'Failed to create cart. Please try again.'
        );
        dispatch(
          handleError(
            cartResponse?.message || 'Failed to create cart. Please try again.'
          )
        );
      }
    } catch (error) {
      console.error('Error processing credit card payment:', error);
      setIsPetPaymentProcessing(false);
      toast.error('Failed to process payment. Please try again.');
      dispatch(handleError('Failed to process payment. Please try again.'));
    }
  };

  const petPermitProceedToPayment = async (): Promise<void> => {
    // Only proceed if this is a pet permit (ID 10)
    if (permit !== '10') {
      console.warn('petPermitProceedToPayment called for non-pet permit');
      return;
    }

    try {
      // Fetch fee details from API
      const feeResponse = await httpRequest(
        'GET',
        `/configuration/fee_details?PermitID=${permit}`
      );

      let taxRate = 0;
      const feeObject = feeResponse?.status === 'success'
        ? getFeesForCurrentPermit(feeResponse.data)
        : {};
      if (feeResponse?.status === 'success') {
        taxRate = feeObject?.CreditCardFee || 0;
      }

      // Get fees from API
      const applicationFeeSpayed = Number(feeObject?.ApplicationFeeSpayed) || 0;
      const applicationFeeNonSpayed = Number(feeObject?.ApplicationFeeNonSpayed) || 0;
      const stateRegistration =
        Number(data?.StateRegistrationFee) ||
        Number(feeObject?.StateRegistrationFeePet) ||
        0;
      const pilotClinic =
        Number(data?.PilotClinicFee) || Number(feeObject?.PilotClinicFeePet) || 0;
      const lateMonthlyFee = Number(feeObject?.LateMonthlyFee) || 0;
      const delinquencyFee = Number(feeObject?.DelinquencyFee) || 0;

      // License Fee = ApplicationFeeSpayed - StateRegistration - PilotClinic
      const licenseFee = Math.max(0, applicationFeeSpayed - stateRegistration - pilotClinic);
      const isSpayed = data?.IsSpayedNeutered === true || data?.IsSpayedNeutered === 1 || data?.IsSpayedNeutered === 'Yes' || data?.IsSpayedNeutered === 'yes';
      const animalPopulationControl = isSpayed ? 0 : Math.max(0, applicationFeeNonSpayed - applicationFeeSpayed);
      const subtotal = licenseFee + stateRegistration + pilotClinic + animalPopulationControl;

      // Late fee from calculatePetLicenseFee (same date precedence as backend getPetFeeBreakdown)
      const applicationDate = data?.ApplicationDate
        ? new Date(data.ApplicationDate)
        : data?.CreateDate
          ? new Date(data.CreateDate)
          : new Date();
      const lateFeesWaived = isPetLateFeesWaived(data?.LateFeesWaived);
      const lateFee = lateFeesWaived
        ? 0
        : getLateFeeAmount(subtotal, lateMonthlyFee, delinquencyFee, applicationDate, isSpayed);
      const total = subtotal + lateFee;
      const taxAmount = total * (taxRate / 100);
      const totalAmount = total + taxAmount;

      const breakdown: PetFeeBreakdown = {
        licenseFee,
        stateRegistration,
        pilotClinic,
        animalPopulationControl: animalPopulationControl || undefined,
        subtotal,
        lateFee,
        total,
        lateFeesWaived
      };

      // Always store calculated values for payment handlers (ref for sync access)
      petPaymentAmountsRef.current = { applicationFee: total, totalAmount, taxRate };
      setCalculatedTotalAmount(totalAmount);
      setPetFeeBreakdown(breakdown);

      // For Manager/Admin: Show payment type modal (Credit Card, Check, Cash)
      // For regular Users: Directly proceed to credit card payment
      if (isManagerOrAdmin) {
        setPaymentTypeModalOpen(true);
      } else {
        await handleCreditCardPayment();
      }
    } catch (error) {
      console.error('Error calculating payment:', error);
      toast.error('Failed to calculate payment. Please try again.');
      dispatch(handleError('Failed to calculate payment. Please try again.'));
    }
  };

  const fetchPetFeeBreakdownForView = async (): Promise<void> => {
    if (permit !== '10' || !data) return;
    try {
      setViewFeeBreakdownLoading(true);
      const feeResponse = await httpRequest(
        'GET',
        `/configuration/fee_details?PermitID=${permit}`
      );
      const feeObject = feeResponse?.status === 'success'
        ? getFeesForCurrentPermit(feeResponse.data)
        : {};
      const taxRate = feeResponse?.status === 'success' ? (feeObject?.CreditCardFee || 0) : 0;
      const applicationFeeSpayed = Number(feeObject?.ApplicationFeeSpayed) || 0;
      const applicationFeeNonSpayed = Number(feeObject?.ApplicationFeeNonSpayed) || 0;
      const stateRegistration =
        Number(data?.StateRegistrationFee) ||
        Number(feeObject?.StateRegistrationFeePet) ||
        0;
      const pilotClinic =
        Number(data?.PilotClinicFee) || Number(feeObject?.PilotClinicFeePet) || 0;
      const lateMonthlyFee = Number(feeObject?.LateMonthlyFee) || 0;
      const delinquencyFee = Number(feeObject?.DelinquencyFee) || 0;
      const licenseFee = Math.max(0, applicationFeeSpayed - stateRegistration - pilotClinic);
      const isSpayed = data?.IsSpayedNeutered === true || data?.IsSpayedNeutered === 1 || data?.IsSpayedNeutered === 'Yes' || data?.IsSpayedNeutered === 'yes';
      const animalPopulationControl = isSpayed ? 0 : Math.max(0, applicationFeeNonSpayed - applicationFeeSpayed);
      const subtotal = licenseFee + stateRegistration + pilotClinic + animalPopulationControl;
      const applicationDate = data?.ApplicationDate
        ? new Date(data.ApplicationDate)
        : data?.CreateDate
          ? new Date(data.CreateDate)
          : new Date();
      const lateFeesWaived = isPetLateFeesWaived(data?.LateFeesWaived);
      const lateFee = lateFeesWaived
        ? 0
        : getLateFeeAmount(subtotal, lateMonthlyFee, delinquencyFee, applicationDate, isSpayed);
      const total = subtotal + lateFee;
      const totalAmount = total + total * (taxRate / 100);
      const breakdown: PetFeeBreakdown = {
        licenseFee,
        stateRegistration,
        pilotClinic,
        animalPopulationControl: animalPopulationControl || undefined,
        subtotal,
        lateFee,
        total,
        lateFeesWaived
      };
      setViewFeeBreakdown(breakdown);
      setViewFeeTotalAmount(totalAmount);
      setFeeBreakdownDialogOpen(true);
    } catch (error) {
      console.error('Error fetching fee breakdown:', error);
      toast.error('Failed to load fee breakdown.');
    } finally {
      setViewFeeBreakdownLoading(false);
    }
  };

  const handleOpenFeeBreakdown = (): void => {
    fetchPetFeeBreakdownForView();
  };

  const handleCloseFeeBreakdown = (): void => {
    setFeeBreakdownDialogOpen(false);
    setViewFeeBreakdown(null);
  };

  // Handle Check payment
  const handleCheckPayment = async (checkNumber: string): Promise<void> => {
    try {
      setIsPetPaymentProcessing(true);

      let applicationFee: number;
      let totalAmount: number;
      let inspectionFee = 0;
      let taxRate = 0;

      if (permit === '10' && petPaymentAmountsRef.current) {
        applicationFee = petPaymentAmountsRef.current.applicationFee;
        totalAmount = petPaymentAmountsRef.current.totalAmount;
        taxRate = petPaymentAmountsRef.current.taxRate;
      } else {
        const feeResponse = await httpRequest(
          'GET',
          `/configuration/fee_details?PermitID=${permit}`
        );
        if (feeResponse?.status === 'success') {
          const feeObject = getFeesForCurrentPermit(feeResponse.data);
          taxRate = feeObject?.CreditCardFee || 0;
        }
        applicationFee = data?.ApplicationFee ? parseFloat(data.ApplicationFee) : 0;
        inspectionFee = data?.InspectionFee ? parseFloat(data.InspectionFee) : 0;
        const subtotal = applicationFee + inspectionFee;
        const taxAmount = subtotal * (taxRate / 100);
        totalAmount = subtotal + taxAmount;
      }

      const departmentName =
        currentPermitDetails?.departmentName || 'Clerk Department';

      const paymentPayload = {
        TownshipID: Number(townshipId),
        PermitID: Number(permit),
        ApplicationID: Number(applicationID),
        UserID: data?.UserID || userData?.UserID,
        ApplicationFee: applicationFee,
        InspectionFee: inspectionFee,
        TaxRateAmount: taxRate,
        TotalAmount: totalAmount,
        DepartmentName: departmentName,
        PetName: data?.PetName || null,
        OwnerName: data?.OwnerName || null,
        PetType: data?.PetType || null,
        CheckNumber: checkNumber
      };

      const response = await httpRequest(
        'POST',
        '/payments/petLicense/check',
        paymentPayload
      );

      if (response?.status === 'success') {
        toast.success('Check payment processed successfully');
        // Refresh application details
        await handleGetApplicationDetails();
        setIsPetPaymentProcessing(false);
      } else {
        setIsPetPaymentProcessing(false);
        toast.error(response?.message || 'Failed to process check payment');
        dispatch(handleError(response?.message || 'Failed to process check payment'));
      }
    } catch (error) {
      console.error('Error processing check payment:', error);
      setIsPetPaymentProcessing(false);
      toast.error('Failed to process check payment. Please try again.');
      dispatch(handleError('Failed to process check payment. Please try again.'));
    }
  };

  // Handle Cash payment
  const handleCashPayment = async (): Promise<void> => {
    try {
      setIsPetPaymentProcessing(true);

      let applicationFee: number;
      let totalAmount: number;
      let inspectionFee = 0;
      let taxRate = 0;

      if (permit === '10' && petPaymentAmountsRef.current) {
        applicationFee = petPaymentAmountsRef.current.applicationFee;
        totalAmount = petPaymentAmountsRef.current.totalAmount;
        taxRate = petPaymentAmountsRef.current.taxRate;
      } else {
        const feeResponse = await httpRequest(
          'GET',
          `/configuration/fee_details?PermitID=${permit}`
        );
        if (feeResponse?.status === 'success') {
          const feeObject = getFeesForCurrentPermit(feeResponse.data);
          taxRate = feeObject?.CreditCardFee || 0;
        }
        applicationFee = data?.ApplicationFee ? parseFloat(data.ApplicationFee) : 0;
        inspectionFee = data?.InspectionFee ? parseFloat(data.InspectionFee) : 0;
        const subtotal = applicationFee + inspectionFee;
        const taxAmount = subtotal * (taxRate / 100);
        totalAmount = subtotal + taxAmount;
      }

      const departmentName =
        currentPermitDetails?.departmentName || 'Clerk Department';

      const paymentPayload = {
        TownshipID: Number(townshipId),
        PermitID: Number(permit),
        ApplicationID: Number(applicationID),
        UserID: data?.UserID || userData?.UserID,
        ApplicationFee: applicationFee,
        InspectionFee: inspectionFee,
        TaxRateAmount: taxRate,
        TotalAmount: totalAmount,
        DepartmentName: departmentName,
        PetName: data?.PetName || null,
        OwnerName: data?.OwnerName || null,
        PetType: data?.PetType || null
      };

      const response = await httpRequest(
        'POST',
        '/payments/petLicense/cash',
        paymentPayload
      );

      if (response?.status === 'success') {
        toast.success('Cash payment processed successfully');
        // Refresh application details
        await handleGetApplicationDetails();
        setIsPetPaymentProcessing(false);
      } else {
        setIsPetPaymentProcessing(false);
        toast.error(response?.message || 'Failed to process cash payment');
        dispatch(handleError(response?.message || 'Failed to process cash payment'));
      }
    } catch (error) {
      console.error('Error processing cash payment:', error);
      setIsPetPaymentProcessing(false);
      toast.error('Failed to process cash payment. Please try again.');
      dispatch(handleError('Failed to process cash payment. Please try again.'));
    }
  };

  // Handler for payment type selection (must be defined after payment handlers)
  const handlePaymentTypeConfirm = async (paymentType: string, checkNumber?: string) => {
    setPaymentTypeModalOpen(false);
    
    if (paymentType === 'Credit Card') {
      await handleCreditCardPayment();
    } else if (paymentType === 'Check') {
      await handleCheckPayment(checkNumber || '');
    } else if (paymentType === 'Cash') {
      await handleCashPayment();
    }
  };

  const constructionPermitProceedToPayment = async (): Promise<void> => {
    try {
      // Set loading state to true
      setLoading(true);
      // Fetch UCC forms data
      const response = await httpRequest(
        'GET',
        // `/construction/ucc_form_share_list?ApplicationID=${applicationID}`
        `/construction/get_ucc_form_fee/${applicationID}`
      );

      // Define interfaces for payment data
      interface PaymentFees {
        [key: string]: number;
      }

      interface PaymentSummary {
        fees: PaymentFees;
        taxRate?: number;
      }

      interface ApplicationDetails {
        userName: string;
        applicationNumber: null;
        applicationId: string | number;
        applicationStatus: string;
        statusPayload: string;
        permitPrefix: string;
      }

      interface PaymentApplicationDetails {
        applicationDetails: ApplicationDetails;
      }

      // Prepare payment summary
      const paymentSummary: PaymentSummary = {
        fees: {}
      };

      // Process the new response format
      if (response?.status === 'success' && response.data?.summary) {
        // Map Type to UCC fee key
        const typeToFeeKey: Record<string, string> = {
          Building: 'buildingUCCFee',
          Electrical: 'electricalUCCFee',
          Mechanical: 'mechanicalUCCFee',
          Plumbing: 'plumbingUCCFee',
          Fire: 'fireUCCFee',
          Chimney: 'chimneyUCCFee'
        };

        // Add fees only if subcode is DATA_FOUND
        response.data.summary.forEach((item: any) => {
          if (item.subcode === 'DATA_FOUND' && item.Fee > 0) {
            const feeKey = typeToFeeKey[item.Type];
            if (feeKey) {
              paymentSummary.fees[feeKey] = item.Fee;
            }
          }
        });
      } else {
        toast.error('Failed to fetch form details');
        return;
      }

      const taxRate = 2;
      // Add tax rate if applicable
      if (taxRate > 0) {
        paymentSummary.taxRate = taxRate;
      }

      // Prepare application details
      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `Nikhil Sharma`,
          applicationNumber: null,
          applicationId: applicationID,
          applicationStatus: 'Approved',
          statusPayload: 'Issued',
          permitPrefix: 'Construction UCC Fee'
        }
      };

      // Navigate to payment page with required data
      navigate(`/payment-methods`, {
        state: { paymentSummary, paymentApplicationDetails }
      });
    } catch (error) {
      console.error('Error processing payment information:', error);
      toast.error('Failed to prepare payment details');
    } finally {
      // Set loading state to false regardless of success or failure
      setLoading(false);
    }
  };

  const handleOpenCertificate = () => {
    setIsModalOpen(true);
  };
  const handleCloseCertificate = () => {
    setIsModalOpen(false);
  };
  interface CertificateData {
    townshipAddress: string;
    dateIssued: string;
    stateCode: string;
    permitNumber: string;
    projectType: string;
    propertyAddress: string;
    block: string;
    lot: string;
    ownerName: string;
    contractor: string;
    constructionOfficialName: string;
    constructionOfficialSignDate: string;
    townshipName: string;
  }
  // Mock save function - replace with your actual API call
  const handleSaveCertificate = async (
    data1: CertificateData,
    signature: string
  ): Promise<void> => {
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Mock API request
      const formData = new FormData();
      formData.append('certificateData', JSON.stringify(data1));

      // Convert signature to blob for upload
      const response = await fetch(signature);
      const blob = await response.blob();
      formData.append('signature', blob, 'signature.png');

      // Replace with your actual API endpoint
      // const result = await fetch('/api/certificates', {
      //   method: 'POST',
      //   body: formData,
      // });

      // if (!result.ok) {
      //   throw new Error('Failed to save certificate');
      // }

      toast.success('Certificate saved and uploaded successfully!');
      // setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Error saving certificate:', error);
      toast.error('Failed to save certificate. Please try again.');
      // setTimeout(() => setErrorMessage(null), 5000);
      throw error;
    }
  };

  return (
    <>
      <Box sx={{ maxWidth: '1400px', margin: 'auto', p: { md: 3, xs: 1 } }}>
        {/* Header Section */}
        <Paper elevation={2} sx={{ p: { md: 2, xs: 1.5 }, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              textAlign={'start'}
              fontSize={{ xs: '15px' }}
            >
              {'Application Details'}
            </Typography>
            {!StaticDetails && (
              <Chip
                icon={statusConfig.icon}
                label={data.ApplicationStatus}
                color={statusConfig.color}
                sx={{
                  fontSize: '0.8rem',
                  px: 1,
                  background:
                    data.ApplicationStatus === 'Issued' ? '#b63dff' : ''
                }}
              />
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 1.5,
              alignItems: 'center'
            }}
          >
            <Typography variant="h5" color="primary" sx={{ mt: 1 }}>
              {data?.ApplicationNumber && (
                <>Application No. : {data?.ApplicationNumber}</>
              )}
              {permit === '10' && data?.LicenseNumber && (
                <>
                  {' '}
                  | License No. : {data?.LicenseNumber}
                </>
              )}
            </Typography>
            {(permitId === 9 || permitId === 10) &&
              data.ApplicationStatus === 'Approved' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <WarningIcon sx={{ color: '#FF2C2C', fontSize: '20px' }} />
                  <Typography
                    variant="h4"
                    sx={{ color: '#FF2C2C', fontWeight: 'large' }}
                  >
                    Payment Due
                  </Typography>
                </Box>
              )}
            {/* {Number(permit) === 9 &&  <Box display='flex' gap={2}>
              {data?.ApplicationStatus === 'Issued' &&<CustomButtonContained
                sx={{
                  mt: { xs: 1.5, sm: 0 },
                  width: { xs: '100%', sm: 'auto' }
                }}
                onClick={() => setInspectionModalOpen(true)}
                title={'Schedule Inspection'}
              />}
              {(data?.ApplicationStatus === 'Issued' || data?.ApplicationStatus === 'Closed Out') &&<CustomButtonContained
                sx={{
                  mt: { xs: 1.5, sm: 0 },
                  width: { xs: '100%', sm: 'auto' }
                }}
                onClick={() => setSwitchTo('View Inspection')}
                title={'View Inspection'}
              />}
            </Box>} */}
            {Number(permit) === 9 &&
              (data?.ApplicationStatus === 'Issued' ||
                data?.ApplicationStatus === 'Closed Out') && (
                <Box display="flex" gap={2}>
                  {data.ApplicationStatus === 'Issued' && (
                    <CustomButtonContained
                      sx={{
                        mt: { xs: 1.5, sm: 0 },
                        width: { xs: '100%', sm: 'auto' }
                      }}
                      onClick={() => setInspectionModalOpen(true)}
                      title="Schedule Inspection"
                    />
                  )}
                  <CustomButtonContained
                    sx={{
                      mt: { xs: 1.5, sm: 0 },
                      width: { xs: '100%', sm: 'auto' }
                    }}
                    onClick={() => setSwitchTo('View Inspection')}
                    title="View Inspection"
                  />
                </Box>
              )}
          </Box>
        </Paper>
        {/* Soft Approval Section - Only on Summary Page */}
        {!isUser && StaticDetails && (
          <SoftApprovalSection
            applicationData={data}
            triggerAnalysis={true}
            onNavigateToSection={(section) => {
              // Navigate back to edit the specific section
              // You could implement this to go back to previous steps
              console.log('User needs to fix section:', section);
              toast.error(`Please review the ${section} section`);
            }}
          />
        )}
        <StyledCard>
          {switchTo !== '' ? (
            <Box sx={{ p: { sm: 2, xs: 1 } }}>
              <EditFormfeatureViews
                switchTo={switchTo}
                setSwitchTo={setSwitchTo}
                applicationDetails={{
                  TownshipID: townshipId,
                  applicationID: applicationID,
                  UserID: data?.UserID,
                  ApplicationStatus: data?.ApplicationStatus
                }}
              />
            </Box>
          ) : (
            <CardContent>
              <FieldGroup gap={{ md: 1, xs: 0 }} mb={{ md: 'auto', xs: 0 }}>
                {relevantFields.map(({ key, value, icon }, index) => (
                  <DetailField
                    key={key}
                    label={
                      key === 'PetDOB'
                        ? 'PetDateOfBirth'
                        : key === 'OwnersName'
                        ? 'Owner Name'
                        : key
                    }
                    value={value}
                    icon={icon}
                    index={index}
                    relevantFields={relevantFields}
                    showFeeBreakdownInfo={key === 'ApplicationFee' && permit === '10'}
                    onFeeBreakdownClick={key === 'ApplicationFee' && permit === '10' ? handleOpenFeeBreakdown : undefined}
                    feeBreakdownLoading={key === 'ApplicationFee' && permit === '10' ? viewFeeBreakdownLoading : undefined}
                  />
                ))}
              </FieldGroup>
              <br />

              {files?.length > 0 && (
                <Box>
                  <Typography
                    variant="h4"
                    component="h1"
                    textAlign={'start'}
                    fontSize={{ xs: '15px' }}
                  >
                    {'Uploaded Documents'}
                  </Typography>
                  {files?.map((file) => (
                    <Box m={1} key={file?.FileName}>
                      <Typography
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                        variant="h6"
                      >
                        <IconButton
                          onClick={() =>
                            file?.downloadURL
                              ? window.open(file.downloadURL)
                              : ''
                          }
                        >
                          <Assignment color="primary" />
                        </IconButton>
                        {file?.Type || file?.FileName}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {permitId === 9 && (
                <FeeSummaryPage applicationID={applicationID} />
              )}

              {permit === '8' && <ViewMachineDetails />}

              {permitId === 9 &&
                data?.ApplicationStatus === 'Closed Out' &&
                data?.CertificateOfApprovalPdfUrl && (
                  <Box mt={4}>
                    <Typography
                      variant="h4"
                      component="h1"
                      textAlign={'start'}
                      fontSize={{ xs: '15px' }}
                    >
                      {'Certificate Of Approval'}
                    </Typography>
                    <Box sx={{ mt: 1, mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        {/* Icon based on file type */}
                        <InsertDriveFileIcon
                          sx={{ mr: 1, color: 'primary.main' }}
                        />

                        {/* File name */}
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {data?.CertificateOfApprovalPdfUrl.split('/').pop() ||
                            `Document ${1 + 1}`}
                        </Typography>

                        {/* Actions */}
                        <Box>
                          {/* View button - opens in new tab */}
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() =>
                                window.open(
                                  data?.CertificateOfApprovalPdfUrl,
                                  '_blank'
                                )
                              }
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* Download button */}
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              component="a"
                              href={data?.CertificateOfApprovalPdfUrl}
                              download={
                                data?.CertificateOfApprovalPdfUrl.split(
                                  '/'
                                ).pop() || `Document ${1 + 1}`
                              }
                            >
                              <GetAppIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

              {data?.ApplicationStatus !== 'Draft' && !StaticDetails && (
                <Box mt={{ xs: 2, sm: 5 }} textAlign={'end'}>
                  {Number(permit) !== 8 && (
                    <CustomButtonContained
                      sx={{
                        ml: { xs: '0', sm: 2 },
                        mt: { xs: 1.5, sm: 0 },
                        width: { xs: '100%', sm: 'auto' }
                      }}
                      onClick={() => setSwitchTo('Upload/View Document')}
                      title={'Upload/View Document'}
                    />
                  )}
                  {Number(permit) !== 5 && (
                    <CustomButtonContained
                      sx={{
                        ml: { xs: '0', sm: 2 },
                        mt: { xs: 1.5, sm: 0 },
                        width: { xs: '100%', sm: 'auto' }
                      }}
                      onClick={() => setSwitchTo('View Payment History')}
                      title={'View Payment History'}
                    />
                  )}
                  <CustomButtonContained
                    sx={{
                      ml: { xs: '0', sm: 2 },
                      mt: { xs: 1.5, sm: 'auto' },
                      width: { xs: '100%', sm: 'auto' }
                    }}
                    title={'View Message History'}
                    onClick={() => setSwitchTo('View Message History')}
                  />
                  {permit === '9' &&
                    data?.ApplicationStatus === 'Approved' &&
                    data?.ApplicationStatus !== 'Issued' && (
                      <CustomButtonContained
                        sx={{
                          ml: { xs: '0', sm: 2 },
                          mt: { xs: 1.5, sm: 'auto' },
                          width: { xs: '100%', sm: 'auto' }
                        }}
                        title={'Proceed To Payment'}
                        onClick={() => constructionPermitProceedToPayment()}
                      />
                    )}
                  {permit === '10' &&
                    data?.ApplicationStatus === 'Approved' &&
                    data?.ApplicationStatus !== 'Issued' &&
                    (canBundleCheckout ? (
                      <CustomButtonContained
                        sx={{
                          ml: { xs: '0', sm: 2 },
                          mt: { xs: 1.5, sm: 'auto' },
                          width: { xs: '100%', sm: 'auto' }
                        }}
                        disabled={isPetInCart}
                        startIcon={
                          isPetInCart ? (
                            <CheckCircleOutlineIcon fontSize="small" />
                          ) : (
                            <ShoppingCartIcon fontSize="small" />
                          )
                        }
                        title={isPetInCart ? 'In Cart' : 'Add to Cart'}
                        onClick={handleAddToPetLicenseCart}
                      />
                    ) : (
                      <CustomButtonContained
                        sx={{
                          ml: { xs: '0', sm: 2 },
                          mt: { xs: 1.5, sm: 'auto' },
                          width: { xs: '100%', sm: 'auto' }
                        }}
                        title={'Proceed To Payment'}
                        onClick={() => petPermitProceedToPayment()}
                      />
                    ))}

                  {isManager && !shouldShowEdit(data) ? (
                    <CustomButtonContained
                      sx={{
                        ml: { xs: '0', sm: 2 },
                        mt: { xs: 1.5, sm: 'auto' },
                        width: { xs: '100%', sm: 'auto' }
                      }}
                      onClick={() => setIsOpen(true)}
                      title={'Send Message'}
                    />
                  ) : null}

                  {((permit === '9' &&
                    data?.ApplicationStatus === 'Approved') ||
                    data?.ApplicationStatus === 'Issued') &&
                    isManager &&
                    permit !== '10' && (
                      <CustomButtonContained
                        sx={{
                          ml: { xs: '0', sm: 2 },
                          mt: { xs: 1.5, sm: 'auto' },
                          width: { xs: '100%', sm: 'auto' }
                        }}
                        title={'Close Out'}
                        onClick={handleOpenCertificate}
                      />
                    )}
                  {isManagerOrAdmin && (() => {
                    // For pet permit (ID 10): Only show when status is 'Issued'
                    if (permit === '10') {
                      return data?.ApplicationStatus === 'Issued';
                    }
                    // For other permits: Show for Approved (except permit 9), Issued, or Closed Out
                    return (
                      (data?.ApplicationStatus === 'Approved' &&
                        permit !== '9') ||
                      data?.ApplicationStatus === 'Issued' ||
                      data?.ApplicationStatus === 'Closed Out'
                    );
                  })() && (
                    // <CustomButtonContained
                    //   sx={{
                    //     ml: { xs: '0', sm: 2 },
                    //     mt: { xs: 1.5, sm: 'auto' },
                    //     width: { xs: '100%', sm: 'auto' }
                    //   }}
                    //   title={'Download License'}
                    //   onClick={() => downloadApplication(applicationID, permit)}
                    // />
                    <CustomButtonContained
                        sx={{
                          ml: { xs: '0', sm: 2 },
                          mt: { xs: 1.5, sm: 'auto' },
                          width: { xs: '100%', sm: 'auto' }
                        }}
                        title={'Print License'}
                        onClick={() => printApplication(applicationID, permit)}
                      />

                  )}
                </Box>
              )}
            </CardContent>
          )}
        </StyledCard>
        <CustomModal
          title={'Send Message'}
          IsOpen={IsOpen}
          setIsOpen={setIsOpen}
          actionButtons={['Cancel', 'Send']}
          loading={loading}
          messageBodyRef={messageBodyRef}
          setLoading={setLoading}
          applicationNumber={data?.ApplicationNumber}
          ApplicationID={applicationID}
          applicationDetails={data}
          content={
            <TextField
              className="txtArea"
              label="Message"
              placeholder="Message"
              name="message"
              multiline
              onChange={(e) => handleMessageChange(e)}
              sx={{ width: '600px' }}
              rows={3}
            />
          }
        />
        {/* Certificate Modal */}
        <CertificateOfApproval
          open={isModalOpen}
          onClose={handleCloseCertificate}
          onSave={handleSaveCertificate}
          applicationData={data}
          handleGetApplicationDetails={handleGetApplicationDetails}
        />
      </Box>
      {Number(permit) === 9 && (
        <ScheduleInspectionModal
          open={inspectionModalOpen}
          onClose={() => setInspectionModalOpen(false)}
          applicationID={applicationID}
          applicationDetails={StaticDetails || data}
        />
      )}
      {/* Payment Confirmation Modal */}
      <CustomModal
        title="Payment Acknowledgement"
        IsOpen={paymentConfirmationModalOpen}
        setIsOpen={setPaymentConfirmationModalOpen}
        actionButtons={[]}
        loading={false}
        content={
          paymentDetails && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Hello <strong>{paymentDetails.userName}</strong>,
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  Your payment of{' '}
                  <strong>
                    ${paymentDetails.totalAmountPayable.toFixed(2)}
                  </strong>{' '}
                  for Application{' '}
                  <strong>#{paymentDetails.applicationNumber}</strong> has been
                  received, including{' '}
                  <strong>
                    ${paymentDetails.totalAmountWithoutTax.toFixed(2)}
                  </strong>{' '}
                  for the Pet License application and{' '}
                  <strong>${paymentDetails.totalTaxAmount.toFixed(2)}</strong>{' '}
                  for online convenience fees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                Your application is currently under process, You will receive your pet license by mail within 8-10 business days.

                </Typography>
                {/* <Typography variant="body1" sx={{ mb: 1.5 }}>
                  Please visit our website to get details on next steps.
                </Typography> */}
              </Box>

              <Typography variant="body1" sx={{ mb: 2 }}>
                If you have any further questions, please contact us at{' '}
                <Link
                  href="mailto:petlicense@bethlehemnj.org"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  petlicense@bethlehemnj.org
                </Link>
                .
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Thank you,
                </Typography>
                <Typography variant="body1">
                  Bethlehem Township
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setPaymentConfirmationModalOpen(false);
                    // Refresh application details
                    if (!StaticDetails) {
                      handleGetApplicationDetails();
                    }
                    // Navigate to application list
                    const departmentName =
                      currentPermitDetails?.departmentName ||
                      'Clerk Department';
                    const basePath =
                      townshipId && permit
                        ? `/${townshipId}/${encodeURIComponent(
                            departmentName
                          )}/${permit}`
                        : '/dashboard';
                    navigate(`${basePath}/application-list`);
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500
                  }}
                >
                  View My Applications
                </Button>
              </Box>
            </Box>
          )
        }
      />
      <PaymentTypeModal
        open={paymentTypeModalOpen}
        onClose={() => {
          setPaymentTypeModalOpen(false);
          setPetFeeBreakdown(undefined);
        }}
        onConfirm={handlePaymentTypeConfirm}
        totalAmount={calculatedTotalAmount}
        petFeeBreakdown={petFeeBreakdown}
      />
      <Dialog open={feeBreakdownDialogOpen} onClose={handleCloseFeeBreakdown} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ position: 'relative', pr: 5 }}>
          Application Fee Breakdown
          <IconButton
            aria-label="close"
            onClick={handleCloseFeeBreakdown}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500]
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewFeeBreakdown && (
            <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">License Fee</Typography>
                <Typography variant="body2">${viewFeeBreakdown.licenseFee.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">State Registration</Typography>
                <Typography variant="body2">${viewFeeBreakdown.stateRegistration.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Pilot Clinic</Typography>
                <Typography variant="body2">${viewFeeBreakdown.pilotClinic.toFixed(2)}</Typography>
              </Box>
              {viewFeeBreakdown.animalPopulationControl !== undefined && viewFeeBreakdown.animalPopulationControl > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Animal Population Control Fee</Typography>
                  <Typography variant="body2">${viewFeeBreakdown.animalPopulationControl.toFixed(2)}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                <Typography variant="body2" fontWeight={600}>Subtotal</Typography>
                <Typography variant="body2" fontWeight={600}>${viewFeeBreakdown.subtotal.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Late Fee
                  {viewFeeBreakdown.lateFeesWaived ? (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                      (waived)
                    </Typography>
                  ) : null}
                </Typography>
                <Typography variant="body2">${viewFeeBreakdown.lateFee.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider', pt: 1, mt: 1 }}>
                <Typography variant="body2" fontWeight={700}>Total Amount to Pay</Typography>
                <Typography variant="h6" color="primary">${viewFeeTotalAmount.toFixed(2)}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ViewApplication;
